import { useEffect } from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { cn } from "@/lib/utils";

export function FlightDetailsSection({
  formData,
  updateForm,
  TRANSFER_LOCATIONS,
  showRadio = true, // can turn off for return if needed
}) {
  const AIRPORT_VALUES = new Set(
    TRANSFER_LOCATIONS.airports.locations.map((l) => l.value),
  );

  const pickupIsAirport = AIRPORT_VALUES.has(formData.pickupLocation);
  const dropoffIsAirport = AIRPORT_VALUES.has(formData.dropoffLocation);

  const bothAvailable =
    formData.tripType === "oneway" && pickupIsAirport && dropoffIsAirport;

  // default to ARRIVAL if both are airports
  useEffect(() => {
    if (bothAvailable && !formData.flightDetailType) {
      updateForm("flightDetailType", "ARRIVAL");
    }

    if (pickupIsAirport && !dropoffIsAirport) {
      if (formData.flightDetailType !== "ARRIVAL") {
        updateForm("flightDetailType", "ARRIVAL");
      }
    }

    // only departure case
    if (!pickupIsAirport && dropoffIsAirport) {
      if (formData.flightDetailType !== "DEPARTURE") {
        updateForm("flightDetailType", "DEPARTURE");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bothAvailable]);

  if (!pickupIsAirport && !dropoffIsAirport) return null;

  const renderArrivalFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label className="text-sm text-slate-600 mb-1 block">
          Arrival Flight Number *
        </Label>
        <Input
          placeholder="e.g., AF1234"
          value={formData.arrivalFlightNo}
          onChange={(e) => updateForm("arrivalFlightNo", e.target.value)}
        />
      </div>
      <div>
        <Label className="text-sm text-slate-600 mb-1 block">
          Arriving From
        </Label>
        <Input
          placeholder="e.g., London Heathrow"
          value={formData.arrivalFrom}
          onChange={(e) => updateForm("arrivalFrom", e.target.value)}
        />
      </div>
    </div>
  );

  const renderDepartureFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label className="text-sm text-slate-600 mb-1 block">
          Departure Flight Number
        </Label>
        <Input
          placeholder="e.g., BA456"
          value={formData.departureFlightNo}
          onChange={(e) => updateForm("departureFlightNo", e.target.value)}
        />
      </div>
      <div>
        <Label className="text-sm text-slate-600 mb-1 block">
          Departing To
        </Label>
        <Input
          placeholder="e.g., New York JFK"
          value={formData.departureTo}
          onChange={(e) => updateForm("departureTo", e.target.value)}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {showRadio && bothAvailable && (
        <div className="flex gap-6">
          {[
            { label: "Arrival", value: "ARRIVAL" },
            { label: "Departure", value: "DEPARTURE" },
          ].map((type) => (
            <label
              key={type.value}
              className={cn(
                "flex items-center gap-2 cursor-pointer text-sm font-medium",
                formData.flightDetailType === type.value
                  ? "text-champagne-gold"
                  : "text-slate-600",
              )}
            >
              <input
                type="radio"
                name="flightDetailType"
                value={type.value}
                checked={formData.flightDetailType === type.value}
                onChange={() => {
                  updateForm("flightDetailType", type.value);

                  if (type.value === "ARRIVAL") {
                    updateForm("departureFlightNo", "");
                    updateForm("departureTo", "");
                  } else {
                    updateForm("arrivalFlightNo", "");
                    updateForm("arrivalFrom", "");
                  }
                }}
                className="accent-champagne-gold"
              />
              {type.label}
            </label>
          ))}
        </div>
      )}

      {bothAvailable ? (
        <>
          {formData.flightDetailType === "ARRIVAL" && renderArrivalFields()}
          {formData.flightDetailType === "DEPARTURE" && renderDepartureFields()}
        </>
      ) : (
        <>
          {pickupIsAirport && renderArrivalFields()}
          {dropoffIsAirport && renderDepartureFields()}
        </>
      )}
    </div>
  );
}
